import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';
import { getMarketing } from '../content/marketing';
import './SiteNav.css';

const SiteNav = ({ transparent = false }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const content = getMarketing(i18n.language);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  const menus = [
    { key: 'product', label: content.nav.product, items: content.nav.menus.product },
    { key: 'resources', label: content.nav.resources, items: content.nav.menus.resources },
    { key: 'company', label: content.nav.company, items: content.nav.menus.company },
  ];

  const activeMenu = menus.find((menu) => menu.key === openMenu);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [i18n.language]);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const open = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  const solid = !transparent || scrolled || mobileOpen || Boolean(openMenu);

  return (
    <header
      className={`site-nav ${solid ? 'is-solid' : 'is-transparent'} ${scrolled ? 'is-scrolled' : ''} ${openMenu ? 'is-mega-open' : ''}`}
      ref={navRef}
      onMouseLeave={scheduleClose}
    >
      <div className="site-nav-bar">
        <div className="site-nav-inner arc-container">
          <Link to="/" className="site-nav-brand" onClick={() => setMobileOpen(false)}>
            <Logo size="medium" variant="full" className={solid ? 'arc-logo-light' : 'arc-logo-on-dark'} />
          </Link>

          <nav className="site-nav-menus" aria-label="Primary">
            {menus.map((menu) => (
              <button
                key={menu.key}
                type="button"
                className={`site-nav-trigger ${openMenu === menu.key ? 'is-open' : ''}`}
                aria-expanded={openMenu === menu.key}
                onMouseEnter={() => open(menu.key)}
                onFocus={() => open(menu.key)}
                onClick={() => setOpenMenu(openMenu === menu.key ? null : menu.key)}
              >
                {menu.label}
                <span className="site-nav-caret" aria-hidden="true" />
              </button>
            ))}
          </nav>

          <div className="site-nav-actions">
            <LanguageSwitcher />
            <button type="button" className="arc-btn arc-btn-ghost site-nav-login" onClick={() => navigate('/login')}>
              {t('nav.login')}
            </button>
            <button type="button" className="arc-btn arc-btn-solid" onClick={() => navigate('/signup')}>
              {t('nav.signup')}
            </button>
            <button
              type="button"
              className="site-nav-burger"
              aria-label="Menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </div>

      <div
        className={`site-mega-panel ${activeMenu ? 'is-open' : ''}`}
        onMouseEnter={() => activeMenu && open(activeMenu.key)}
      >
        {activeMenu && (
          <div className="site-mega-inner arc-container">
            <div className="site-mega-label">{activeMenu.label}</div>
            <div className="site-mega-grid">
              {activeMenu.items.map((item) => (
                <Link
                  key={item.to + item.title}
                  to={item.to}
                  className="site-mega-link"
                  onClick={() => setOpenMenu(null)}
                >
                  <strong>{item.title}</strong>
                  <span>{item.desc}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="site-nav-mobile">
          {menus.map((menu) => (
            <div key={menu.key} className="site-nav-mobile-group">
              <p>{menu.label}</p>
              {menu.items.map((item) => (
                <Link key={item.to + item.title} to={item.to} onClick={() => setMobileOpen(false)}>
                  <strong>{item.title}</strong>
                  <span>{item.desc}</span>
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </header>
  );
};

export default SiteNav;
